"use client"

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { copy } from "@/lib/copy"

interface FeedbackDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (comment: string) => void
  feedbackType: "positive" | "negative"
}

export function FeedbackDialog({ isOpen, onClose, onSubmit, feedbackType }: FeedbackDialogProps) {
  const [comment, setComment] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      await onSubmit(comment)
      setComment("")
      onClose()
    } catch (error) {
      console.error("Error submitting feedback:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setComment("")
    onClose()
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {feedbackType === "positive"
              ? copy.feedback.dialogTitlePositive
              : copy.feedback.dialogTitleNegative}
          </DialogTitle>
          <DialogDescription>{copy.feedback.dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <textarea
            id="feedback-comment"
            value={comment}
            onChange={e => setComment(e.target.value)}
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
            rows={4}
            placeholder={copy.feedback.dialogPlaceholder}
            maxLength={5000}
          />
          <div className="text-right text-xs text-muted-foreground">{comment.length} / 5000</div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            {copy.feedback.cancel}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? copy.feedback.sending : copy.feedback.send}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
